// AdminApiKeysPage — 第八十批：用户 API key 的管理面（需求方裁：会话等价 +
// 默认禁管理面 + **仅管理员代签**）。
//
// ── 为什么这一页只在管理面上存在 ────────────────────────────────────────────
// 「本人自助签发」是最顺手的形态，也正是这一版**不做**的那个：一枚会话等价的
// 凭据一旦能自助生成，它就成了绕开登录流程的第二条入口，而登录流程上挂着验证码、
// 设备窗口、口令年龄这一整套判据。代签把这件事留在一个有人负责的动作上，
// 并且 `createdBy` 把"谁签的"钉在行里。
//
// ── 明文只出现一次 ──────────────────────────────────────────────────────────
// 后端只存 sha256，签发响应里的明文是**这枚 key 在世上唯一的一份**。所以：
// 不写 localStorage、不进 useLoad 的缓存、不随列表重载复现——它只活在
// `issued` 这个组件状态里，页面一关就没了。提示文案必须把这件事说死，
// 否则管理员会以为"回头再来这一页复制"，而那时只剩一行哈希。
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputLabel, MenuItem,
  Paper, Select, Stack, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import api, { cliVersionHints, onCliVersionHints } from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import {
  CLIENT_BADGE_COLOR, CLIENT_BADGE_LABEL, KEY_STATE_COLOR, KEY_STATE_LABEL, cliVersionRefs,
  clientBadge, expiresInDaysFrom, issueResultKind, keyState, mayOfferOnboarding,
  onboardingPayload,
} from './apiKeys.mjs';
import { normalizeCliManifest } from './cliDownload.mjs';
// 第二百零七批（SB-180 (e) 实撞）：密送落点组——多组收件人必须显式选定，
// 判断在 deliverGroup.mjs 一处定形，Git 访问控制发放面共用同一份。
import {
  deliveryGroupOptions, deliveryToGroupId, groupListOf, needsGroupChoice,
} from './deliverGroup.mjs';

function fmtMs(ms) {
  if (ms == null) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

export default function AdminApiKeysPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();

  const accounts = useLoad(() => api.adminListAccounts(), []);
  const accountList = Array.isArray(accounts.data?.accounts) ? accounts.data.accounts : [];
  // 第二百零七批（SB-180 (e)）：组列表只为密送落点组下拉取名——账号行里只有
  // 组 id，光靠它下拉里全是 #3 这种谜语。取不到时选项回落 `#<id>`，下拉照常可选。
  const groups = useLoad(() => api.adminProjectGroups().catch(() => null), []);
  const groupList = groupListOf(groups.data);

  const [accountId, setAccountId] = useState('');
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('');
  const [busy, setBusy] = useState(false);
  // ── 第一百零九批 C2（裁定 1）：不当时显示，改为密递给他 ──────────────────
  // 勾上之后系统自动签一件**指名给该账户**的一次性密件（72 小时内有效——需求方裁，
  // 与站内信延迟发送的上限同值；过期就重铸），而签发响应里
  // `key` 这个键**整个不出现**——管理员从头到尾看不到明文。这解的是一个具体的场景：
  // 交接时管理员与本人不在同一个时区/同一场会议，明文只出现一次的窗口对不上。
  const [deliver, setDeliver] = useState(false);
  // 第二百零七批（SB-180 (e) 实撞）：密送落点组的选择。多组收件人不选就发正是
  // 撞过的形状（后端自动取首组，distill 凭据落进 MirrorMii 组）——现在多组必选，
  // 单组不展示下拉但请求照带唯一组 id。
  const [deliverGroupId, setDeliverGroupId] = useState('');
  // 明文只活在这里。刷新列表不会让它回来——库里没有第二份。
  const [issued, setIssued] = useState(null);
  const issuedKind = issueResultKind(issued);

  const keys = useLoad(
    () => (accountId ? api.adminApiKeyList(accountId) : Promise.resolve({ keys: [] })),
    [accountId],
  );
  const keyList = Array.isArray(keys.data?.keys) ? keys.data.keys : [];
  const now = Date.now();

  // ── 第一百八十三批：「客户端」列的两枚参照 ─────────────────────────────────
  // 当前版从 CLI 清单取（下载页同一只 normalizeCliManifest），最低支持版从每条应答的
  // X-DC-CLI-Min-Version 头取——头在 request() 里被记下，这里只订阅。清单失败**不**
  // 算这一页的错误：徽记只是软提醒，参照缺席时列里仍照显示原文，只是不出徽记。
  const manifest = useLoad(() => api.cliManifest().catch(() => null), []);
  const [hints, setHints] = useState(cliVersionHints);
  useEffect(() => onCliVersionHints(setHints), []);
  const refs = cliVersionRefs(normalizeCliManifest(manifest.data), hints);

  const errMsg = (e, fallback) =>
    t(errKey(e, { byStatus: { 409: 'admin.apiKeys.disabledAccount' }, fallback }));

  // ── "这个账号没有组，密件没有落点" 要在**点之前**说 ──────────────────────
  // 后端对这一种也回 409 CONFLICT（与"账号已停用"同码同状态），所以事后按码分辨是
  // 分不开的——两句话的下一步动作却完全相反（一个是别签，一个是先把人加进组）。
  // 而这件事在页面上是**已知的**：账号列表的每行都带 projectGroupIds。于是把判断
  // 提到勾选之前：勾不动 + 一句说清怎么修，而不是让他点一次再猜是哪一种冲突。
  const selected = accountList.find((a) => String(a.id) === String(accountId));
  const noGroup = Boolean(selected)
    && (!Array.isArray(selected.projectGroupIds) || selected.projectGroupIds.length === 0);
  // ── 第二百零七批（SB-180 (e)）：多组收件人的落点组也要在**点之前**说 ──────
  // toGroupId 是随请求发出的显式落点（单组=唯一那个组，多组=下拉里选定的那个）；
  // 多组没选定时它是 null，签发按钮拦住——不选就发正是后端"自动取首组"撞出
  // 事故的那个形状。
  const toGroupId = deliver && !noGroup ? deliveryToGroupId(selected, deliverGroupId) : null;
  const needGroupPick = deliver && !noGroup && needsGroupChoice(selected) && toGroupId == null;

  const onIssue = async () => {
    if (busy || !accountId || needGroupPick) return;
    setBusy(true);
    setIssued(null);
    try {
      const r = await api.adminApiKeyIssue(accountId, {
        label: label.trim(),
        expiresInDays: expiresInDaysFrom(days),
        // 只在真勾了的时候送这一位：一个恒送 false 的字段会让"这条路走没走过"
        // 在请求日志里读不出来。落点组显式随单（第二百零七批）：单组收件人也带
        // ——显式落点让后端对单组/多组走同一条校验路，不留一半继续走
        // "自动取首组"的老路。
        ...(deliver && !noGroup
          ? { deliverInstead: true, ...(toGroupId != null ? { toGroupId } : {}) }
          : {}),
      });
      setIssued(r);
      setLabel('');
      setDays('');
      notify(t('admin.apiKeys.issued'), 'success');
      keys.reload();
    } catch (e) {
      notify(errMsg(e, 'admin.apiKeys.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── SB-148 C：onboarding 快捷发送 ─────────────────────────────────────────
  // 每行自己的发送态（keyId → 'sending' | 'sent'），不挪用全局 busy：要防的只是
  // **同一行**的重复点击，把整页锁死是另一回事。'sent' 留在行上——snackbar 会自己
  // 消失，而「我到底给他发过没有」两分钟后还会被问一次。
  const [onboarding, setOnboarding] = useState({});
  const sendOnboarding = async (k) => {
    if (onboarding[k.id] === 'sending') return;   // 防重复点击：第二下直接吞掉
    setOnboarding((m) => ({ ...m, [k.id]: 'sending' }));
    try {
      await api.adminSendTemplateMessage(onboardingPayload(selected, k));
      setOnboarding((m) => ({ ...m, [k.id]: 'sent' }));
      notify(t('admin.apiKeys.onboarding.sentOk'), 'success');
    } catch (e) {
      // 失败把这一行清回**可点**：呈现错误 + 原按钮就是重试入口，绝不留一个既非
      // 已发也非可发的僵态。刻意不走 errMsg——那张表把 409 映成"账号已停用"，
      // 那是签发面的话，模板端点的 409 不一定是它。
      setOnboarding((m) => {
        const next = { ...m };
        delete next[k.id];
        return next;
      });
      // 400 带上后端拒因原文（AdminSettings 同一姿态）：这类拒绝换个姿势重试不会
      // 变好——标签超长/像 key/与收件人无共同组，拒因里写着该去改什么。吞掉它
      // 就只剩一句"请重试"，而重试永远失败、没人知道为什么。
      const detail = e?.status === 400 && e?.message ? '：' + e.message : '';
      notify(t(errKey(e, {
        byStatus: { 400: 'admin.apiKeys.onboarding.rejected' },
        fallback: 'admin.apiKeys.onboarding.error',
      })) + detail, 'error');
    }
  };

  const onRevoke = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.adminApiKeyRevoke(id);
      notify(t('admin.apiKeys.revokedOk'), 'success');
      keys.reload();
    } catch (e) {
      notify(errMsg(e, 'admin.apiKeys.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const stateChip = (k) => {
    const s = keyState(k, now);
    return <Chip size="small" color={KEY_STATE_COLOR[s]} label={t(KEY_STATE_LABEL[s])} />;
  };

  // 客户端列：原文照显（非 dc-cli 的 UA 也显示，管理员要看得出"这把 key 在被 curl
  // 用"），徽记只在 clientBadge 判出 behind/upgrade 时出现——判断在纯函数里，这里
  // 不重写第二份。Tooltip 把两枚参照摆出来：一个只写"落后"的黄标会让人去猜落后谁。
  const clientCell = (k) => {
    const text = typeof k.lastClient === 'string' && k.lastClient.trim() ? k.lastClient : '—';
    const badge = clientBadge(k.lastClient, refs);
    const chip = CLIENT_BADGE_LABEL[badge] ? (
      <Tooltip title={t('admin.apiKeys.client.refs', {
        cur: refs.current || '—', min: refs.min || '—',
      })}>
        <Chip size="small" color={CLIENT_BADGE_COLOR[badge]} label={t(CLIENT_BADGE_LABEL[badge])} />
      </Tooltip>
    ) : null;
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', overflowWrap: 'anywhere' }}>
          {text}
        </Box>
        {chip}
      </Stack>
    );
  };

  return (
    <Box>
      <AdminSubnav />
      {/* 第八十八批：删掉"← 账号管理"这条回跳。需求方点名的是账号管理与 Git 访问控制
          之间那两条，而这一条**是同一个缺陷的第三份副本**——同样的 AdminSubnav 遗留物、
          同样一套只有它自己用的文案键。修掉三分之二再宣布做完，正是需求方在反对的形状。 */}
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('admin.apiKeys.title')}
        <HelpLink section="admin-keys" />
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>{t('admin.apiKeys.scopeNote')}</Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('admin.apiKeys.issue')}
        </Typography>
        <Stack sx={{ alignItems: { md: 'center' } }} direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* xs 下 260px 地板几乎顶满内容宽——改为整行铺开，md 起才回到 260 地板 */}
          <FormControl size="small" sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
            <InputLabel id="api-key-account-label">{t('admin.apiKeys.account')}</InputLabel>
            <Select
              labelId="api-key-account-label"
              label={t('admin.apiKeys.account')}
              value={accountId}
              // 换收件人必须清落点组选择：上一位的组 id 对这一位可能不存在，
              // 更糟的是**恰好也存在**——那就是把密件落进错组的第二种走法。
              onChange={(e) => {
                setAccountId(e.target.value); setDeliverGroupId(''); setIssued(null);
              }}
              disabled={busy}
            >
              {accountList.map((a) => (
                // whiteSpace normal：Popover 纸面横向只裁不滚，「名字 · 邮箱」在 360px 下会被静默裁掉
                <MenuItem key={a.id} value={a.id} sx={{ whiteSpace: 'normal' }}>
                  {a.displayName ? `${a.displayName} · ${a.email}` : a.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label={t('admin.apiKeys.label')} value={label}
            onChange={(e) => setLabel(e.target.value)} disabled={busy} />
          <TextField size="small" type="number" label={t('admin.apiKeys.expiresInDays')}
            value={days} onChange={(e) => setDays(e.target.value)} disabled={busy}
            helperText={t('admin.apiKeys.expiresHint')} sx={{ maxWidth: 200 }} />
          <Button variant="contained" onClick={onIssue}
            disabled={busy || !accountId || needGroupPick}>
            {t('admin.apiKeys.issue')}
          </Button>
        </Stack>
        {/* ── 延迟领取（第一百零九批 C2，裁定 1）───────────────────────────
            勾上以后系统自动签一件**指名给该账户**的一次性密件，72 小时内有效；
            管理员从头到尾看不到明文。账号不属于任何项目组时勾不动——密件是
            (组, 人) 两道闸，没有组就没有落点，而那句话要在点之前说。 */}
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={deliver && !noGroup}
                disabled={busy || !accountId || noGroup}
                onChange={(e) => { setDeliver(e.target.checked); setIssued(null); }}
              />
            }
            label={t('admin.apiKey.deliverInstead')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {noGroup ? t('admin.apiKey.noGroup') : t('admin.apiKey.deliverHelper')}
          </Typography>
          {/* ── 落点组下拉（第二百零七批，SB-180 (e) 实撞）：只对多组收件人出现，
              必选。单组收件人不出下拉——唯一落点没有选错的可能，请求仍带唯一
              组 id（toGroupId 显式随单）。选项名取自组列表，取不到回落 #id。 */}
          {deliver && !noGroup && needsGroupChoice(selected) ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
              sx={{ mt: 1, alignItems: { md: 'center' } }}>
              <FormControl size="small"
                sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
                <InputLabel id="api-key-deliver-group-label">
                  {t('admin.apiKey.deliverGroup')}
                </InputLabel>
                <Select
                  labelId="api-key-deliver-group-label"
                  label={t('admin.apiKey.deliverGroup')}
                  value={deliverGroupId}
                  onChange={(e) => { setDeliverGroupId(e.target.value); setIssued(null); }}
                  disabled={busy}
                >
                  {deliveryGroupOptions(selected, groupList, lang).map((o) => (
                    <MenuItem key={o.id} value={o.id} sx={{ whiteSpace: 'normal' }}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {t('admin.apiKey.deliverGroupHint')}
              </Typography>
            </Stack>
          ) : null}
        </Box>

        {issuedKind === 'delivered' ? (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setIssued(null)}>
            {t('admin.apiKey.delivered', {
              id: issued.delivery.id,
              who: issued.delivery.toAccount || '—',
              t: fmtMs(issued.delivery.expiresAtMs),
            })}
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              {t('admin.apiKey.deliveredHint')}
            </Box>
          </Alert>
        ) : issuedKind === 'plaintext' ? (
          <Alert severity="warning" sx={{ mt: 2, wordBreak: 'break-all' }}
            onClose={() => setIssued(null)}>
            {t('admin.apiKey.plaintextWarning')}
            <Box component="code" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
              {issued.key}
            </Box>
          </Alert>
        ) : issued ? (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setIssued(null)}>
            {t('admin.apiKeys.error')}
          </Alert>
        ) : null}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('admin.apiKeys.usageNote')}
        </Typography>
      </Paper>

      {accounts.status === 'error' ? (
        <Alert severity="error">{errMsg(accounts.error, 'admin.apiKeys.error')}</Alert>
      ) : keys.status === 'error' ? (
        <Alert severity="error">{errMsg(keys.error, 'admin.apiKeys.error')}</Alert>
      ) : !accountId ? (
        <Typography color="text.secondary">{t('admin.apiKeys.pickAccount')}</Typography>
      ) : keyList.length === 0 && keys.status === 'ready' ? (
        <Typography color="text.secondary">{t('admin.apiKeys.empty')}</Typography>
      ) : (
        // 第一百八十三批加了「客户端」列（等宽字 + 徽记），六列在 718px 的平板内容盒里
        // 已经挤不下：与其让每列自己换行，不如让表在平板上横滚（DataTable 本来就为此
        // 而设）。860 是 1024 起的桌面盒能容下的宽度。
        <DataTable minWidth={860}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.apiKeys.label')}</TableCell>
                <TableCell>{t('admin.apiKeys.status')}</TableCell>
                <TableCell>{t('admin.apiKeys.createdAt')}</TableCell>
                <TableCell>{t('admin.apiKeys.expiresAt')}</TableCell>
                <TableCell>{t('admin.apiKeys.lastUsed')}</TableCell>
                <TableCell>{t('admin.apiKeys.client')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keyList.map((k) => (
                <TableRow key={k.id}>
                  <TableCell>{k.label || '—'}</TableCell>
                  <TableCell>{stateChip(k)}</TableCell>
                  <TableCell>{fmtMs(k.createdAtMs)}</TableCell>
                  <TableCell>{k.expiresAtMs == null ? t('admin.apiKeys.never')
                    : fmtMs(k.expiresAtMs)}</TableCell>
                  <TableCell>{k.lastUsedAtMs == null ? t('admin.apiKeys.neverUsed')
                    : fmtMs(k.lastUsedAtMs)}</TableCell>
                  <TableCell>{clientCell(k)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5}
                      sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                      {/* SB-148 C：last_used 空且 key 生效 ⇒ 快捷发送。判在
                          mayOfferOnboarding（含登记在案的 active 收窄），这里不重写第二份。 */}
                      {mayOfferOnboarding(k, now) ? (
                        onboarding[k.id] === 'sent' ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('admin.apiKeys.onboarding.sent')}
                          </Typography>
                        ) : (
                          <Button size="small" onClick={() => sendOnboarding(k)}
                            disabled={busy || onboarding[k.id] === 'sending'}>
                            {t('admin.apiKeys.onboarding.send')}
                          </Button>
                        )
                      ) : null}
                      {k.enabled ? (
                        <Button size="small" color="error" onClick={() => onRevoke(k.id)}
                          disabled={busy}>
                          {t('admin.apiKeys.revoke')}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
        </DataTable>
      )}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}
